import { connect } from 'react-redux'
import { gql, graphql } from 'react-apollo'
import { filter as fuzzySearch } from 'fuzzaldrin'
import { intersection } from 'lodash'
import getProp from '@f/get-prop'
import { compose, lifecycle, mapProps, withProps, withPropsOnChange } from 'recompose'
import { withLoader } from '@segment/react-ui-library'
import withError from '../hoc/withError'
import {
  setCatalogOverviewSettings,
  setCurrentPageName,
  trackIntegrationsCatalogTileClick
} from 'actions'
import { getCatalogOverviewSettings } from 'selectors'
import { integrationsCatalogOverviewPage } from 'analytics'
import Overview from '../components/Catalog/Overview'

// GraphQL
const withData = graphql(gql`
  query getIntegrationsCatalog {
    me {
      id
      isAdmin
    }
    integrationMetadatas {
      id
      slug
      name
      logos
      categories
      platforms
      public
    }
  }
`, {
  props: ({ data }) => ({
    metadatas: data.integrationMetadatas,
    isAdmin: getProp('me.isAdmin', data),
    isLoading: data.loading, // Changed to work with `withLoader`
    error: data.error,
    refetch: data.refetch
  })
})

// Error handling for GraphQL request
const withErrorHandling = withError(({ refetch }) => ({
  title: 'Couldn\'t fetch integrations',
  messaging: 'An error occurred and we were unable to fetch integrations',
  primaryButton: {
    children: 'Try Again',
    onClick: () => refetch()
  }
}))

// Redux
const withState = connect(
  (state, ownProps) => ({
    metadataBaseUrl: `/${ownProps.params.workspaceSlug}/integrations/catalog/`,
    ...getCatalogOverviewSettings(state)
  }),
  (dispatch) => ({
    onSearchChange: searchTerm => dispatch(setCatalogOverviewSettings({ searchTerm })),
    onCategoryFilter: filterCategory => dispatch(setCatalogOverviewSettings({ filterCategory })),
    onPlatformFilter: filterPlatform => dispatch(setCatalogOverviewSettings({ filterPlatform })),
    onTileClick: integrationSlug => dispatch(trackIntegrationsCatalogTileClick(integrationSlug)),
    onMount: () => dispatch(setCurrentPageName('integrations-catalog'))
  })
)

// Dispatch onMount
const withOnMount = lifecycle({
  componentDidMount: function () {
    this.props.onMount()
    integrationsCatalogOverviewPage()
  }
})

// Filter out private metadatas unless user `isAdmin`
const withFilteredPrivateMetadas = withPropsOnChange(['metadatas', 'isAdmin'], ({ metadatas, isAdmin }) => ({
  metadatas: metadatas.filter(metadata => metadata.public || isAdmin)
}))

// Add `categories` prop which is derived from metadatas
const withCategories = withPropsOnChange(['metadatas'], ({ metadatas = [] }) => {
  const categories = metadatas
    .reduce((memo, metadata) => {
      for (const category of metadata.categories) {
        if (!memo.includes(category)) memo.push(category)
      }
      return memo
    }, [])
    .sort()
    .map(value => ({ label: value, value }))

  categories.unshift({
    label: 'All Categories',
    value: 'all'
  })

  return { categories }
})

// Additional static props
const withAdditionalProps = withProps({
  platforms: [
    { label: 'All Platforms', value: 'all' },
    { label: 'Browser', value: 'browser' },
    { label: 'Mobile', value: 'mobile' },
    { label: 'Server', value: 'server' },
    { label: 'Cloud', value: 'cloud' }
  ],
  name: 'Integration',
  description: `
    Below are all of the Integrations available with Segment.
    Select them to learn more. You can connect them to any compatible Source.
  `,
  requestUrl: 'https://segment.com/contact/requests/integration'
})

// The remaining higher-order-components are focused on the filtering of
// `props.metadatas`. Each HOC adds a new prop of filtered metadatas. The
// last HOC merges them all together, replacing `props.metadatas`.

// Filtered metadatas: search
const withSearchTermMetadatas = withPropsOnChange(['metadatas', 'searchTerm'], props => ({
  metadatasSearchTerm: fuzzySearch(props.metadatas, props.searchTerm, { key: 'name' })
}))

// Filtered metadatas: category
const withFilterCategoryMetadatas = withPropsOnChange(['metadatas', 'filterCategory'], props => ({
  metadatasFilterCategory: props.filterCategory !== 'all'
    ? props.metadatas.filter(({ categories }) => categories.includes(props.filterCategory))
    : props.metadatas
}))

// Filtered metadatas: platform
const withFilterPlatformMetadatas = withPropsOnChange(['metadatas', 'filterPlatform'], props => ({
  metadatasFilterPlatform: props.filterPlatform !== 'all'
    ? props.metadatas.filter(({ platforms }) => platforms[props.filterPlatform])
    : props.metadatas
}))

// Merge all above filters back into the `metadatas` prop
const withMergedFilters = mapProps(({
  metadatasSearchTerm,
  metadatasFilterCategory,
  metadatasFilterPlatform,
  searchTerm,
  ...props
}) => ({
  ...props,
  searchTerm,
  metadatas: searchTerm
    ? intersection(metadatasSearchTerm, metadatasFilterCategory, metadatasFilterPlatform)
    : intersection(metadatasFilterCategory, metadatasFilterPlatform)
}))

// Compose together all filtering related HOCs.
const withFilteredMetadatas = compose(
  withSearchTermMetadatas,
  withFilterCategoryMetadatas,
  withFilterPlatformMetadatas,
  withMergedFilters
)

export default compose(
  withData,
  withLoader,
  withErrorHandling,
  withState,
  withOnMount,
  withFilteredPrivateMetadas,
  withCategories,
  withAdditionalProps,
  withFilteredMetadatas
)(Overview)



// WEBPACK FOOTER //
// ./containers/IntegrationsCatalogOverviewApp.js